import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Table,
  Input,
  Button,
  Spinner,
} from "reactstrap";
import axiosInstance from "../../axiosInstance";
import { URL } from "../../api.jsx";
import getPrivilegeList from "../PrivilegeList";
import * as XLSX from "xlsx";

const PAGE_SIZE = 10; // ⬅️ change if needed

const CallerList = () => {
  const privilegeList = getPrivilegeList();
  const [exportingId, setExportingId] = useState(null);

  /* ================= STATES ================= */
  const [callers, setCallers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [callerId, setCallerId] = useState("");

  const [page, setPage] = useState(0); // ⬅️ frontend page

  /* ================= API CALL ================= */
  const fetchCallers = async () => {
    try {
      setLoading(true);

      const res = await axiosInstance.get(URL.CALLER_LIST, {
        params: {
          groupId: 250,
          userId: callerId || null,
        },
      });

      setCallers(res.data || []);
      setPage(0); // ⬅️ reset page on search
    } catch (err) {
      console.error("Failed to fetch callers", err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= CALLER REPORT EXPORT ================= */
  const exportCallerReport = async (caller) => {
    try {
      setExportingId(caller.user_id);
      const res = await axiosInstance.get(URL.CALLER_SUMMARY, {
        params: { callerId: caller.user_id },
      });
      const d = res.data;

      const exportData = [
        { Field: "Caller ID", Value: d.callerId },
        { Field: "Total Calls Today", Value: d.totalCallsToday },
        { Field: "Connected Calls Today", Value: d.connectedCallsToday },
        { Field: "Disconnected Calls Today", Value: d.disconnectedCallsToday },
        { Field: "Connected Rate Today (%)", Value: d.connectedRateToday },
        { Field: "PTP Today", Value: d.ptpToday },
        { Field: "Paid Calls Today", Value: d.paidCallsToday },
        { Field: "Total Calls All Time", Value: d.totalCallsAllTime },
        { Field: "Pending Cases", Value: d.pendingCases },
        { Field: "Total Recovery Amount", Value: d.totalRecoveryAmount },
      ];

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Caller Report");
      XLSX.writeFile(wb, `Caller_Report_${caller.user_id}_${caller.name || ""}.xlsx`);
    } catch {
      alert("Failed to export caller report");
    } finally {
      setExportingId(null);
    }
  };

  /* ================= INIT ================= */
  useEffect(() => {
    fetchCallers();
  }, []);

  /* ================= PAGINATION LOGIC ================= */
  const totalPages = Math.ceil(callers.length / PAGE_SIZE);

  const paginatedCallers = callers.slice(
    page * PAGE_SIZE,
    page * PAGE_SIZE + PAGE_SIZE
  );

  /* ================= UI ================= */
  return (
    <>
      {/* ================= PAGE TITLE ================= */}
      <Row className="mb-2">
        <Col>
          <h4 className="page-title">Caller List</h4>
        </Col>
      </Row>

      {/* ================= FILTER ================= */}
      <Card className="mb-3 p-3">
        <div className="d-flex gap-2 align-items-end flex-wrap">
          <div>
            <label className="form-label mb-1">Filter Type</label>
            <select className="form-select form-select-sm" disabled>
              <option>Caller ID</option>
            </select>
          </div>

          <div>
            <label className="form-label mb-1">Caller ID</label>
            <Input
              bsSize="sm"
              value={callerId}
              onChange={(e) => setCallerId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") fetchCallers();
              }}
              style={{ width: 180 }}
              placeholder="Enter Caller ID"
            />
          </div>

          <Button size="sm" color="info" onClick={fetchCallers}>
            Search
          </Button>

          <Button
            size="sm"
            color="secondary"
            onClick={() => {
              setCallerId("");
              fetchCallers();
            }}
          >
            Reset
          </Button>
        </div>
      </Card>

      {/* ================= TABLE ================= */}
      <Card>
        <CardBody>
          {loading ? (
            <div className="text-center my-4">
              <Spinner color="primary" />
            </div>
          ) : (
            <>
              <Table bordered hover responsive size="sm">
                <thead className="table-light">
                  <tr>
                    <th>Caller ID</th>
                    <th>Name</th>
                    <th>User Name</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Department</th>
                    <th>Status</th>
                    <th>Created Date</th>
                    {privilegeList.callerReportView && <th>Caller Report</th>}
                  </tr>
                </thead>

                <tbody>
                  {paginatedCallers.length === 0 ? (
                    <tr>
                      <td colSpan={privilegeList.callerReportView ? 9 : 8} className="text-center text-muted">
                        No callers found
                      </td>
                    </tr>
                  ) : (
                    paginatedCallers.map((c) => (
                      <tr key={c.user_id}>
                        <td>{c.user_id}</td>
                        <td>{c.name}</td>
                        <td>{c.user_name}</td>
                        <td>{c.phone_number}</td>
                        <td>{c.email}</td>
                        <td>{c.dept}</td>
                        <td>
                          {c.is_active === 1 ? (
                            <span className="badge bg-success">Active</span>
                          ) : (
                            <span className="badge bg-danger">Inactive</span>
                          )}
                        </td>
                        <td>{c.create_date}</td>
                        {privilegeList.callerReportView && (
                          <td>
                            <Button
                              size="sm"
                              color="success"
                              disabled={exportingId === c.user_id}
                              onClick={() => exportCallerReport(c)}
                            >
                              {exportingId === c.user_id ? "Exporting..." : "Export"}
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>

              {/* ================= PAGINATION ================= */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-end align-items-center gap-2 mt-2">
                  <Button
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage(page - 1)}
                  >
                    Prev
                  </Button>

                  <span className="text-muted">
                    Page {page + 1} of {totalPages}
                  </span>

                  <Button
                    size="sm"
                    disabled={page + 1 >= totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default CallerList;
